import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL!;
const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!;

const supabase = createClient(supabaseUrl, supabaseAnonKey);

export interface PaymentConfirmationPayload {
  customerEmail: string;
  customerName?: string;
  orderId: string;
  orderNumber?: string;
  placement: string;
  regions: string[];
  total: number;
  term: 'one' | 'three';
  artworkDeadline: string;
  deliveryDate: string;
}

export interface ArtworkReminderPayload {
  customerEmail: string;
  customerName?: string;
  orderId: string;
  placement: string;
  regions: string[];
  artworkDeadline: string;
  daysUntilDeadline: number;
}

export interface AdminNotificationPayload {
  customerEmail: string;
  customerName?: string;
  orderId: string;
  orderNumber?: string;
  placement: string;
  regions: string[];
  total: number;
  term: 'one' | 'three';
  artworkDeadline: string;
  deliveryDate: string;
}

export interface CampaignDeliveryPayload {
  customerEmail: string;
  customerName?: string;
  orderId: string;
  orderNumber?: string;
  placement?: string;
  regions?: string[];
  issueLabel?: string;
  deliveryDate?: string;
  distributionCount?: number;
}

export interface PaymentReceiptPayload {
  customerEmail: string;
  customerName?: string;
  orderId: string;
  amount: string;
  date?: string;
}

export interface DeliveryProofPayload {
  recipientEmail: string;
  additionalRecipients?: string[];
  businessName?: string;
  booksDelivered: number;
  flyersIncluded?: number | string;
  driverName?: string;
  timestamp?: string;
  photoUrl?: string | null;
}

export interface PrintShippedPayload {
  customerEmail: string;
  customerName?: string;
  orderId: string;
  orderNumber?: string;
  placement?: string;
  regions?: string[];
  issueLabel?: string;
  shipDate?: string;
}

export interface DeliveryDispatchedPayload {
  customerEmail: string;
  customerName?: string;
  orderId: string;
  orderNumber?: string;
  placement?: string;
  regions?: string[];
  issueLabel?: string;
  shipDate?: string;
  statusUrl?: string;
}

// ── New order-status email payloads ─────────────────────────────────────────

export interface OrderStatusEmailPayload {
  eventType: 'artwork_received' | 'artwork_approved' | 'campaign_live' | 'payment_failed' | 'deadline_approaching';
  customerEmail: string;
  customerName?: string;
  orderId: string;
  orderNumber?: string;
  placement?: string;
  regions?: string[];
  total?: number;
  term?: 'one' | 'three';
  artworkDeadline?: string;
  deliveryDate?: string;
  daysUntilDeadline?: number;
  failureReason?: string;
}

export async function sendOrderStatusEmail(payload: OrderStatusEmailPayload): Promise<{ success: boolean; error?: string }> {
  try {
    const { data, error } = await supabase.functions.invoke('send-order-status', {
      body: payload,
    });

    if (error) {
      console.error(`Order status email error (${payload.eventType}):`, error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Unknown error';
    console.error(`Order status email failed (${payload.eventType}):`, message);
    return { success: false, error: message };
  }
}

export async function sendPaymentConfirmationEmail(payload: PaymentConfirmationPayload): Promise<{ success: boolean; error?: string }> {
  try {
    const { data, error } = await supabase.functions.invoke('send-payment-confirmation', {
      body: payload,
    });

    if (error) {
      console.error('Payment confirmation email error:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Unknown error';
    console.error('Payment confirmation email failed:', message);
    return { success: false, error: message };
  }
}

export async function sendArtworkReminderEmail(payload: ArtworkReminderPayload): Promise<{ success: boolean; error?: string }> {
  try {
    const { data, error } = await supabase.functions.invoke('send-artwork-reminder', {
      body: payload,
    });

    if (error) {
      console.error('Artwork reminder email error:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Unknown error';
    console.error('Artwork reminder email failed:', message);
    return { success: false, error: message };
  }
}

export async function sendAdminNotificationEmail(payload: AdminNotificationPayload): Promise<{ success: boolean; error?: string }> {
  try {
    const { data, error } = await supabase.functions.invoke('send-admin-notification', {
      body: payload,
    });

    if (error) {
      console.error('Admin notification email error:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Unknown error';
    console.error('Admin notification email failed:', message);
    return { success: false, error: message };
  }
}

export async function sendOrderConfirmationEmail(
  payload: PaymentConfirmationPayload
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data, error } = await supabase.functions.invoke('send-order-confirmation', {
      body: payload,
    });
    if (error) {
      console.error('Order confirmation email error:', error);
      return { success: false, error: error.message };
    }
    return { success: true };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Unknown error';
    console.error('Order confirmation email failed:', message);
    return { success: false, error: message };
  }
}

export async function sendCampaignDeliveryEmail(
  payload: CampaignDeliveryPayload
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data, error } = await supabase.functions.invoke('send-campaign-delivery', {
      body: payload,
    });
    if (error) {
      console.error('Campaign delivery email error:', error);
      return { success: false, error: error.message };
    }
    return { success: true };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Unknown error';
    console.error('Campaign delivery email failed:', message);
    return { success: false, error: message };
  }
}

export async function sendPaymentReceiptEmail(
  payload: PaymentReceiptPayload
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data, error } = await supabase.functions.invoke('send-payment-receipt', {
      body: payload,
    });
    if (error) {
      console.error('Payment receipt email error:', error);
      return { success: false, error: error.message };
    }
    return { success: true };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Unknown error';
    console.error('Payment receipt email failed:', message);
    return { success: false, error: message };
  }
}

export async function sendDeliveryProofEmail(
  payload: DeliveryProofPayload
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data, error } = await supabase.functions.invoke('send-delivery-proof', {
      body: payload,
    });
    if (error) {
      console.error('Delivery proof email error:', error);
      return { success: false, error: error.message };
    }
    return { success: true };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Unknown error';
    console.error('Delivery proof email failed:', message);
    return { success: false, error: message };
  }
}

export async function sendPrintShippedEmail(
  payload: PrintShippedPayload
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data, error } = await supabase.functions.invoke('send-print-shipped', {
      body: payload,
    });
    if (error) {
      console.error('Print shipped email error:', error);
      return { success: false, error: error.message };
    }
    return { success: true };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Unknown error';
    console.error('Print shipped email failed:', message);
    return { success: false, error: message };
  }
}

export async function sendDeliveryDispatchedEmail(
  payload: DeliveryDispatchedPayload
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data, error } = await supabase.functions.invoke('send-delivery-dispatched', {
      body: payload,
    });
    if (error) {
      console.error('Delivery dispatched email error:', error);
      return { success: false, error: error.message };
    }
    return { success: true };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Unknown error';
    console.error('Delivery dispatched email failed:', message);
    return { success: false, error: message };
  }
}

export interface BonusEarnedPayload {
  managerEmail: string;
  managerName?: string;
  orderId: string;
  orderNumber?: string;
  placementId: string;
  regionId?: string;
  bonusCents: number;
  bonusType?: string;
  bonusValue?: number;
  walletBalanceCents?: number;
}

export async function sendBonusEarnedEmail(
  payload: BonusEarnedPayload
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data, error } = await supabase.functions.invoke('send-bonus-earned', {
      body: payload,
    });
    if (error) {
      console.error('Bonus earned email error:', error);
      return { success: false, error: error.message };
    }
    return { success: true };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Unknown error';
    console.error('Bonus earned email failed:', message);
    return { success: false, error: message };
  }
}

export interface PayoutReceiptPayload {
  managerEmail: string;
  managerName?: string;
  payoutRequestId?: string;
  amountCents: number;
  transferId?: string;
  payoutDate?: string;
}

export async function sendPayoutReceiptEmail(
  payload: PayoutReceiptPayload
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data, error } = await supabase.functions.invoke('send-payout-receipt', {
      body: payload,
    });
    if (error) {
      console.error('Payout receipt email error:', error);
      return { success: false, error: error.message };
    }
    return { success: true };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Unknown error';
    console.error('Payout receipt email failed:', message);
    return { success: false, error: message };
  }
}

/**
 * Schedule artwork deadline reminders at T-10, T-7, T-3 days.
 * Call this after a successful order to queue all three reminders.
 */
export async function scheduleArtworkReminders(payload: Omit<ArtworkReminderPayload, 'daysUntilDeadline'>): Promise<void> {
  const reminderDays = [10, 7, 3];

  for (const days of reminderDays) {
    await sendArtworkReminderEmail({ ...payload, daysUntilDeadline: days });
  }
}

// ── Advertising Agreement Email ──────────────────────────────────────────────

export interface AdvertisingAgreementPayload {
  customerEmail: string;
  customerName?: string;
  orderId?: string;
  orderNumber?: string;
  placement?: string;
  regions?: string[];
  total?: number;
  agreementUrl?: string;
}

/**
 * Send the ZipAdBook Advertising Agreement to a customer for signing.
 * Used by admins for phone-order customers who need to sign the agreement.
 */
export async function sendAdvertisingAgreementEmail(
  payload: AdvertisingAgreementPayload
): Promise<{ success: boolean; error?: string }> {
  try {
    const RESEND_API_KEY = process.env.RESEND_API_KEY;
    // Use the Resend API directly via the send-order-confirmation edge function
    // which already has Resend configured
    const { data, error } = await supabase.functions.invoke('send-order-confirmation', {
      body: {
        ...payload,
        customerEmail: payload.customerEmail,
        customerName: payload.customerName || 'Valued Advertiser',
        orderId: payload.orderId || '',
        orderNumber: payload.orderNumber || '',
        placement: payload.placement || '',
        regions: payload.regions || [],
        total: payload.total || 0,
        term: 'one' as const,
        artworkDeadline: '',
        deliveryDate: '',
        emailType: 'advertising_agreement',
        agreementUrl: payload.agreementUrl || `${process.env.NEXT_PUBLIC_SITE_URL || 'https://zipadbook.com'}/terms-of-service`,
      },
    });
    if (error) {
      console.error('Advertising agreement email error:', error);
      return { success: false, error: error.message };
    }
    return { success: true };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Unknown error';
    console.error('Advertising agreement email failed:', message);
    return { success: false, error: message };
  }
}

// ── Order Milestone Emails ────────────────────────────────────────────────────

export type OrderMilestone =
  | 'booking_confirmation' |'artwork_received' |'payment_processed' |'fulfillment_complete';

/**
 * Send a transactional email for a key order milestone.
 *
 * Milestones:
 *  - booking_confirmation  → order confirmed + artwork deadline reminder
 *  - artwork_received      → artwork uploaded, under review
 *  - payment_processed     → payment successfully processed
 *  - fulfillment_complete  → campaign fully distributed
 *
 * This calls the /api/orders/milestone-emails Next.js route which
 * resolves the customer email from the order and dispatches the
 * appropriate Resend edge function.
 */
export async function sendMilestoneEmail(
  orderId: string,
  milestone: OrderMilestone
): Promise<{ success: boolean; emailId?: string; error?: string }> {
  try {
    const siteUrl =
      typeof window !== 'undefined'
        ? window.location.origin
        : process.env.NEXT_PUBLIC_SITE_URL || 'https://zipadbook.com';

    const res = await fetch(`${siteUrl}/api/orders/milestone-emails`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orderId, milestone }),
    });

    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      const message = errData?.error || `HTTP ${res.status}`;
      console.error(`[sendMilestoneEmail] ${milestone} failed for order ${orderId}:`, message);
      return { success: false, error: message };
    }

    const data = await res.json().catch(() => ({}));
    return { success: true, emailId: data?.emailId };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Unknown error';
    console.error(`[sendMilestoneEmail] ${milestone} exception for order ${orderId}:`, message);
    return { success: false, error: message };
  }
}